/**
 * CheckoutForm.js — Ship / Pickup contact+address form (kind "checkout-form").
 *
 * UI parity with old wineengine shippingForm (ss1):
 *   bot bubble title (from checkout.js) + Contact card + Delivery Address card,
 *   two-column rows for name / city+postcode.
 *
 *   ship   → email + delivery address fields
 *   pickup → email only
 *
 * Wine Engine (old main.js): no company / state / phone; country defaults to GB;
 * UK postcode validation.
 *
 * Country/province from api.getDeliveryCountries. Emits
 * ws-checkout-submit { type, fields } on success.
 * Listens for ws-checkout-error to re-enable Continue (old shippingForm stayed
 * editable when BE returned field errors).
 *
 * message.data: { type: "ship"|"pickup", values?: {...prefill for Edit} }
 */

import { on, emit } from "../../../core/state.js";
import { getTenantId } from "../../../core/configAccessors.js";
import * as api from "../../../core/api.js";

function isWineEngineTenant() {
  const id = String(getTenantId() || "").toLowerCase();
  return id === "wineengine" || id === "thewineengine";
}

function isValidUKPostcode(postcode) {
  const normalized = String(postcode || "").trim().toUpperCase();
  return /^([A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}|GIR\s*0AA)$/.test(normalized);
}

function cleanField(value) {
  if (value == null) return "";
  const text = String(value).trim();
  return text === "undefined" || text === "null" ? "" : text;
}

function fieldGroup({
  name,
  label,
  placeholder,
  required = false,
  select = null,
  values = {},
  refs,
  countrySel,
  provinceSel,
}) {
  const group = document.createElement("div");
  group.className = "ws-checkout-field";

  if (label) {
    const lab = document.createElement("label");
    // Old Fogarty: "Company <span>(optional)</span>"
    if (/\(optional\)\s*$/i.test(label)) {
      const base = label.replace(/\s*\(optional\)\s*$/i, "").trim();
      lab.appendChild(document.createTextNode(`${base} `));
      const opt = document.createElement("span");
      opt.textContent = "(optional)";
      lab.appendChild(opt);
    } else {
      lab.textContent = label;
    }
    group.appendChild(lab);
  }

  let input;
  if (select) {
    input = document.createElement("select");
    if (select === "country") countrySel.el = input;
    if (select === "province") provinceSel.el = input;
  } else {
    input = document.createElement("input");
    input.type = name === "phone" ? "tel" : "text";
    if (placeholder) input.placeholder = placeholder;
  }
  input.name = name;
  input.className = "ws-checkout-input";
  const prefill = cleanField(values[name]);
  if (prefill) input.value = prefill;
  group.appendChild(input);

  const err = document.createElement("small");
  err.className = "ws-checkout-error";
  group.appendChild(err);

  refs[name] = { input, err, required: !!required };
  return group;
}

function section(titleText) {
  const sec = document.createElement("div");
  sec.className = "ws-checkout-section";
  const head = document.createElement("div");
  head.className = "ws-checkout-section-head";
  const title = document.createElement("p");
  title.className = "ws-checkout-section-title";
  title.textContent = titleText;
  head.appendChild(title);
  sec.appendChild(head);
  const fields = document.createElement("div");
  fields.className = "ws-checkout-fields";
  sec.appendChild(fields);
  return { sec, fields };
}

function row(cols = 2) {
  const el = document.createElement("div");
  el.className = `ws-checkout-row ws-checkout-row--${cols}`;
  return el;
}

export default class CheckoutForm {
  render(message) {
    const type = message?.data?.type === "pickup" ? "pickup" : "ship";
    const values = message?.data?.values || {};
    const isEdit = Object.keys(values).length > 0;
    const wineEngine = isWineEngineTenant();

    const form = document.createElement("form");
    form.className = "ws-checkout-form";
    form.noValidate = true;

    const refs = {};
    const countrySel = { el: null };
    const provinceSel = { el: null };
    const ctx = { values, refs, countrySel, provinceSel };

    // ── Contact ──
    const contact = section("Contact");
    contact.fields.appendChild(
      fieldGroup({
        name: "email",
        label: null,
        placeholder: "Enter email id",
        required: true,
        ...ctx,
      }),
    );
    form.appendChild(contact.sec);

    if (type === "ship") {
      const delivery = section("Delivery Address");

      const countryRow = row(1);
      countryRow.appendChild(
        fieldGroup({
          name: "country",
          label: "Country/Region",
          required: true,
          select: "country",
          ...ctx,
        }),
      );
      delivery.fields.appendChild(countryRow);

      const nameRow = row(2);
      nameRow.appendChild(
        fieldGroup({
          name: "firstName",
          label: "First Name",
          placeholder: "Enter your first name",
          required: true,
          ...ctx,
        }),
      );
      nameRow.appendChild(
        fieldGroup({
          name: "lastName",
          label: "Last Name",
          placeholder: "Enter your last name",
          required: true,
          ...ctx,
        }),
      );
      delivery.fields.appendChild(nameRow);

      // Fogarty (old shippingForm): Company (optional) after name fields.
      // Wine Engine omits company.
      if (!wineEngine) {
        const companyRow = row(1);
        companyRow.appendChild(
          fieldGroup({
            name: "company",
            label: "Company (optional)",
            placeholder: "Enter company name",
            ...ctx,
          }),
        );
        delivery.fields.appendChild(companyRow);
      }

      const addrRow = row(1);
      addrRow.appendChild(
        fieldGroup({
          name: "address1",
          label: "Address",
          placeholder: "Enter Address",
          required: true,
          ...ctx,
        }),
      );
      delivery.fields.appendChild(addrRow);

      const aptRow = row(1);
      aptRow.appendChild(
        fieldGroup({
          name: "address2",
          label: "Apartment, suite, etc(optional)",
          placeholder: "Apartment, suite, etc. (optional)",
          ...ctx,
        }),
      );
      delivery.fields.appendChild(aptRow);

      const cityRow = row(2);
      cityRow.appendChild(
        fieldGroup({
          name: "city",
          label: "City",
          placeholder: "Enter city name",
          required: true,
          ...ctx,
        }),
      );
      cityRow.appendChild(
        fieldGroup({
          name: "zip",
          label: "Postcode",
          placeholder: "Enter Postcode",
          required: true,
          ...ctx,
        }),
      );
      delivery.fields.appendChild(cityRow);

      // Fogarty / other tenants: state + phone. Wine Engine omits both (old form).
      let stateRow = null;
      if (!wineEngine) {
        stateRow = row(1);
        stateRow.classList.add("ws-checkout-province-row");
        stateRow.hidden = true;
        stateRow.appendChild(
          fieldGroup({
            name: "province",
            label: "State",
            required: false,
            select: "province",
            ...ctx,
          }),
        );
        delivery.fields.appendChild(stateRow);

        const phoneRow = row(1);
        phoneRow.appendChild(
          fieldGroup({
            name: "phone",
            label: "Phone (e.g. +12025550100)",
            placeholder: "+12025550100",
            required: true,
            ...ctx,
          }),
        );
        delivery.fields.appendChild(phoneRow);
      }

      form.appendChild(delivery.sec);

      this._loadCountries(
        countrySel.el,
        provinceSel.el,
        values,
        stateRow,
        refs,
        wineEngine,
      );
    }

    const footer = document.createElement("div");
    footer.className = "ws-checkout-footer";
    const submit = document.createElement("button");
    submit.type = "submit";
    submit.className = "ws-checkout-submit";
    submit.textContent = isEdit ? "Update" : "Continue to Checkout";
    footer.appendChild(submit);
    form.appendChild(footer);

    let pending = false;

    form.addEventListener("submit", (e) => {
      e.preventDefault();
      const { ok, data } = this._validate(refs, wineEngine);
      // Keep Continue enabled when client validation fails (old form never
      // disabled the button on required-field errors).
      if (!ok) {
        pending = false;
        submit.disabled = false;
        return;
      }
      pending = true;
      submit.disabled = true;
      // Fogarty keeps empty company for Shopify; wineengine never sends company.
      if (type === "ship" && !wineEngine && data.company == null) {
        data.company = "";
      }
      emit("ws-checkout-submit", { type, fields: data });
    });

    // API / BE failure — re-enable Continue and show field errors (old parity).
    on("ws-checkout-error", ({ fieldErrors } = {}) => {
      if (!form.isConnected || !pending) return;
      pending = false;
      submit.disabled = false;
      if (fieldErrors && typeof fieldErrors === "object") {
        Object.entries(fieldErrors).forEach(([name, msg]) => {
          const ref = refs[name];
          if (ref?.err) ref.err.textContent = String(msg || "");
        });
      }
    });

    // Success — keep disabled like old fieldset.disabled after showOrderDetails.
    on("ws-checkout-success", () => {
      if (!form.isConnected || !pending) return;
      pending = false;
      submit.disabled = true;
      form.querySelectorAll("input, select, button").forEach((el) => {
        el.disabled = true;
      });
    });

    return form;
  }

  async _loadCountries(countryEl, provinceEl, values, stateRow, refs, wineEngine) {
    let countries = [];
    try {
      const res = await api.getDeliveryCountries();
      countries = Array.isArray(res?.countries)
        ? res.countries
        : Array.isArray(res)
          ? res
          : [];
    } catch {
      /* leave selects empty */
    }
    if (!countries.length || !countryEl) return;

    // Wine Engine: default United Kingdom (GB). Others: placeholder until chosen.
    const defaultCountry = wineEngine
      ? countries.find((c) => c.isoCode === "GB" || c.name === "United Kingdom")
          ?.isoCode ||
        "GB"
      : "";
    const selectedCountry =
      cleanField(values.country) || defaultCountry;

    const ph = document.createElement("option");
    ph.value = "";
    ph.textContent = "Select Country";
    ph.disabled = true;
    if (!selectedCountry) ph.selected = true;
    countryEl.appendChild(ph);

    countries.forEach((c) => {
      const opt = document.createElement("option");
      opt.value = c.isoCode;
      opt.textContent = c.name;
      if (selectedCountry && selectedCountry === c.isoCode) opt.selected = true;
      countryEl.appendChild(opt);
    });

    if (selectedCountry && countryEl.value !== selectedCountry) {
      countryEl.value = selectedCountry;
    }

    if (wineEngine) return; // no state field

    const fillProvinces = (isoCode, selected) => {
      if (!provinceEl) return;
      provinceEl.replaceChildren();
      const country = countries.find((c) => c.isoCode === isoCode);
      const provinces = country?.availableProvinces || [];

      if (stateRow) stateRow.hidden = provinces.length === 0;
      if (refs.province) refs.province.required = provinces.length > 0;

      const p0 = document.createElement("option");
      p0.value = "";
      p0.textContent = "Select State";
      p0.disabled = true;
      p0.selected = !selected;
      provinceEl.appendChild(p0);

      provinces.forEach((p) => {
        const opt = document.createElement("option");
        opt.value = p.isoCode;
        opt.textContent = p.name;
        if (selected && selected === p.isoCode) opt.selected = true;
        provinceEl.appendChild(opt);
      });
    };

    countryEl.addEventListener("change", () =>
      fillProvinces(countryEl.value, null),
    );
    if (selectedCountry) fillProvinces(selectedCountry, values.province);
  }

  _validate(refs, wineEngine = false) {
    let ok = true;
    const data = {};
    Object.entries(refs).forEach(([name, ref]) => {
      const { input, err, required } = ref;
      const value = cleanField(input.value);
      data[name] = value;
      const provinceHidden =
        name === "province" &&
        input.closest(".ws-checkout-province-row")?.hidden;
      if (required && !provinceHidden && !value) {
        err.textContent = "This field is required";
        ok = false;
      } else if (wineEngine && name === "zip" && value && !isValidUKPostcode(value)) {
        err.textContent = "Please enter a valid UK postcode.";
        ok = false;
      } else {
        err.textContent = "";
      }
    });
    // Never emit empty province / company / phone for wineengine.
    if (wineEngine) {
      delete data.province;
      delete data.company;
      delete data.phone;
    }
    return { ok, data };
  }
}
